import { useMemo, useState } from "react";
import {
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
} from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import { Button } from "newui/components/Button";
import { showToast } from "utils/helpers";
import {
	useRoles,
	useAssignmentMutations,
} from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface AssignRoleDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	user: UserAssignment | null;
}

// §4.2.1 last-System-Admin guard — DELETE role returns 409 {error:"rbac.last_system_admin", message}.
const LAST_SYSTEM_ADMIN_CODE = "rbac.last_system_admin";
const LAST_SYSTEM_ADMIN_FALLBACK =
	"This is the company's last System Administrator — assign the System Administrator role to another user before removing it here.";

// Narrow an unknown mutation error to the last-System-Admin 409, returning its server message (if any).
const lastSystemAdminMessage = (error: unknown): string | null => {
	const response = (error as { response?: { status?: number; data?: { error?: string; message?: string } } })
		?.response;
	if (response?.status === 409 && response.data?.error === LAST_SYSTEM_ADMIN_CODE) {
		return response.data.message || LAST_SYSTEM_ADMIN_FALLBACK;
	}
	return null;
};

// Secondary path: per-user role management (add a single role, remove existing assignments).
// The multi-user quick-assign wizard is the primary flow.
export const AssignRoleDrawer = ({ isOpen, onClose, user }: AssignRoleDrawerProps) => {
	const { roles } = useRoles();
	const { assignRoleMutation, removeRoleMutation } = useAssignmentMutations();
	const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const roleOptions = roles.map((role) => ({ label: role.name, value: role.id }));
	const selectedRoleOption =
		roleOptions.find((option) => option.value === selectedRoleId) ?? null;

	const assignments = useMemo(() => user?.assignments ?? [], [user]);

	const handleSave = async () => {
		if (!user || selectedRoleId === null) {
			return;
		}
		setError(null);
		try {
			await assignRoleMutation.mutateAsync({
				userId: user.id,
				// Company-wide role assignment; finer scope is managed via the scope grants flow.
				payload: { role_id: selectedRoleId, scope_type: "global" },
			});
			setSelectedRoleId(null);
		} catch (saveError) {
			setError(
				saveError instanceof Error ? saveError.message : "Failed to assign role."
			);
		}
	};

	const handleRemove = (assignmentId: number) => {
		if (!user) {
			return;
		}
		removeRoleMutation.mutate(
			{ userId: user.id, assignmentId },
			{
				onError: (removeError) => {
					// VT-006: surface the last-System-Admin 409 as a specific toast (server copy verbatim
					// when present). Other errors keep the existing (silent) fallback behaviour.
					const message = lastSystemAdminMessage(removeError);
					if (message !== null) {
						showToast(message, "Cannot remove role", false, LAST_SYSTEM_ADMIN_CODE, "error");
					}
				},
			}
		);
	};

	return (
		<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
			<DrawerOverlay />
			<DrawerContent data-testid="assign-role-drawer">
				<DrawerCloseButton data-testid="drawer-close" />
				<DrawerHeader>
					{user ? `Manage roles — ${user.name}` : "Manage roles"}
				</DrawerHeader>
				<DrawerBody>
					{error && (
						<div
							className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
							data-testid="drawer-error"
						>
							{error}
						</div>
					)}
					<div className="mb-4">
						<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
							Current roles
						</p>
						{assignments.length === 0 ? (
							<p className="text-sm text-gray-500" data-testid="drawer-no-roles">
								No roles assigned yet.
							</p>
						) : (
							<ul className="flex flex-col gap-1.5" data-testid="drawer-role-list">
								{assignments.map((assignment) => (
									<li
										key={assignment.assignment_id}
										className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2"
									>
										<span className="text-sm">
											<span className="font-medium">{assignment.role_name}</span>
											{assignment.scope_label && (
												<span className="ml-2 text-xs text-gray-500">
													{assignment.scope_label}
												</span>
											)}
										</span>
										<button
											type="button"
											onClick={() => handleRemove(assignment.assignment_id)}
											aria-label={`Remove ${assignment.role_name}`}
											data-testid={`drawer-remove-${assignment.assignment_id}`}
											className="text-gray-400 hover:text-red-600"
										>
											<FiTrash2 size={16} />
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
					<SelectWithLabel
						label="Add role (company-wide)"
						required
						testId="drawer-role-select"
						options={roleOptions}
						value={selectedRoleOption}
						onChange={(option) =>
							setSelectedRoleId(
								option && !Array.isArray(option) ? (option.value as number) : null
							)
						}
						placeholder="Select a role…"
					/>
					<p className="mt-1.5 text-xs text-gray-500" data-testid="drawer-scope-note">
						Roles added here are assigned company-wide. Use the assign wizard to scope a role
						to specific projects or RFx events.
					</p>
				</DrawerBody>
				<DrawerFooter>
					<Button
						btntype="plain-border"
						size="sm"
						onClick={onClose}
						margin="0 8px 0 0"
						data-testid="drawer-cancel"
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						size="sm"
						disabled={selectedRoleId === null || assignRoleMutation.isPending}
						isLoading={assignRoleMutation.isPending}
						onClick={handleSave}
						data-testid="drawer-save"
					>
						Save
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
};
